import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "wedding_admin_pwd";

/**
 * useAdminAuth
 * Stores admin password in sessionStorage so all admin pages stay logged in
 * during a browser session. Verifies with backend on first use.
 */
export const useAdminAuth = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [checking, setChecking] = useState(!!sessionStorage.getItem(STORAGE_KEY));
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setChecking(false);
      setAuthed(false);
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/admin/login`, { password: stored });
        if (!cancelled) {
          setPassword(stored);
          setAuthed(true);
        }
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
        if (!cancelled) {
          setPassword("");
          setAuthed(false);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (pwd) => {
    await axios.post(`${API}/admin/login`, { password: pwd });
    sessionStorage.setItem(STORAGE_KEY, pwd);
    setPassword(pwd);
    setAuthed(true);
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    navigate("/admin");
  };

  return { password, authed, checking, login, logout };
};

/**
 * LoginScreen - shared login UI for all admin pages
 */
export const LoginScreen = ({ onSubmit }) => {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    setError("");
    try {
      await onSubmit(pwd);
    } catch (err) {
      setError(err?.response?.data?.detail || "Falsches Passwort");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-screen"
      className="min-h-screen bg-sage-50 flex items-center justify-center px-6"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-sage-200 rounded-2xl shadow-xl p-8 md:p-10"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sage-100 text-sage-600 mb-4">
            <Lock size={22} />
          </div>
          <h1 className="font-serif text-3xl text-sage-800">Admin-Bereich</h1>
          <p className="mt-2 text-sage-600 text-sm">Nico &amp; Nicole · Hochzeit</p>
        </div>
        <label className="block mb-5">
          <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
            Passwort
          </span>
          <input
            data-testid="admin-password-input"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full rounded-lg border border-sage-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
            autoFocus
          />
        </label>
        {error && (
          <p data-testid="admin-error" className="mb-4 text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-3 text-sm uppercase tracking-[0.25em] transition-colors"
        >
          {loading ? "Prüfe..." : "Einloggen"}
        </button>
        <a
          href="/"
          className="block text-center mt-6 text-sage-500 hover:text-sage-700 text-sm underline"
        >
          ← Zurück zur Hochzeitsseite
        </a>
      </form>
    </div>
  );
};
